import { useEffect, useRef } from 'react';
import { PopupState, tooltipConfig, WidgetPopupType } from '@/modules/models';
import { usePausableTimeout } from '@/modules/widget/hooks/usePausableTimeout';
import { bridgeAtom, useValue, widgetPopupsStatesAtom } from '@/store';

const DEFAULT_DELAY = 2500;
const DEFAULT_DURATION = 5000;

interface UseCrossSalePopupProps {
  isDisabled: boolean;
  isHover: boolean;
  startPopupWithDelay: (popupType: WidgetPopupType, delay: number) => void;
  setPopupState: (popupType: WidgetPopupType, state: PopupState) => void;
  hidePopup: () => void;
}

export const useCrossSalePopup = ({
  isDisabled,
  isHover,
  startPopupWithDelay,
  hidePopup,
  setPopupState,
}: UseCrossSalePopupProps) => {
  const { widget } = useValue(bridgeAtom);
  const widgetPopupsStates = useValue(widgetPopupsStatesAtom);

  const startedRef = useRef(false);

  const { crossSaleTooltip } = widget || {};
  const { CROSS_SALE } = widgetPopupsStates;

  const delay = (crossSaleTooltip as tooltipConfig)?.delay || DEFAULT_DELAY;
  const duration = (crossSaleTooltip as tooltipConfig)?.duration || DEFAULT_DURATION;
  const pauseOnHover = Boolean((crossSaleTooltip as tooltipConfig)?.pauseOnHover);

  const { start } = usePausableTimeout(isHover, pauseOnHover, hidePopup);

  useEffect(() => {
    if (CROSS_SALE !== PopupState.STARTED || isDisabled || startedRef.current) {
      return;
    }
    startedRef.current = true;
    startPopupWithDelay(WidgetPopupType.CROSS_SALE, delay);
  }, [isDisabled, CROSS_SALE, delay, startPopupWithDelay]);

  useEffect(() => {
    if (CROSS_SALE !== PopupState.VISIBLE || isDisabled) {
      return;
    }

    start(duration);
    setPopupState(WidgetPopupType.CROSS_SALE, PopupState.SEEN);
  }, [isDisabled, CROSS_SALE, start, duration, setPopupState]);

  return null;
};

export { DEFAULT_DURATION as CROSS_SALE_DEFAULT_DURATION, DEFAULT_DELAY as CROSS_SALE_DEFAULT_DELAY };
